











































































































import {
    defineComponent,
    computed,
    onMounted,
    onBeforeUnmount,
    ref,
} from '@vue/composition-api';
import {
    useI18n,
    useVueRouter,
    useVuexStore,
    isMobileLayout,
} from '@superbet-group/web.lib.vue-utilities';
import { RouteName } from '@src/app/router/constants/RouteName';
import { AuthenticationStore } from '@store/modules/data/authentication/authenticationIndex';
import { InboxStore } from '@store/modules/ui/inbox/inboxIndex';
import HeaderLink from './HeaderLink.vue';
import { capitalize } from '@superbet-group/web.lib.utilities';
import {
    SdsFlag,
    SdsIcon,
} from '@superbet-group/web.lib.ui-components';
import { NavigationDataStore } from '@store/modules/navigation';
import AccountUserInfoAvatar from '@src/app/shared/components/Account/AccountUserInfoAvatar.vue';
import { featureFlagService } from '@superbet-group/web.lib.flags';
import { appLocale } from '@src/config/locale';
import { Analytics as AnalyticsSDK } from '@superbet-group/web.analytics.sdk';
import { PromotionHubEntryEventLabels } from '@src/app/promotionHub/shared/types/PromotionHubEntryLabels';
import { SiteLanguageConfig } from '@modules/app/siteLanguage/siteLanguageConfig';
import {
    isSocialAppEnabled,
    isSocialEnabledOrAppEnabled,
} from '@socialFederation/helpers';
import { shellLocalConfig } from '@src/config/shell/localConfig';

export default defineComponent({
    name: 'AccountMenuHeader',
    emits: ['hideDropdown'],
    props: {
        isPage: {
            type: Boolean,
            required: true,
        },
    },
    setup() {
        const { translate } = useI18n();
        const { pushRoute } = useVueRouter();

        const authModule = useVuexStore<AuthenticationStore>('data/authentication');
        const userId = authModule.getter('userId');
        const user = authModule.getter('user');

        const inboxIndex = useVuexStore<InboxStore>('ui/inbox');
        const numberOfNewMessages = inboxIndex.state('numberOfNewMessages');

        const navigationModule = useVuexStore<NavigationDataStore>('navigation');
        const currentNavigationContext = navigationModule.getter('currentNavigationContext');

        const selectedLocaleObject = SiteLanguageConfig.availableLocales.find((locale) => locale.value === appLocale.locale);

        const headerLinks = computed(() => [
            {
                tag: 'notifications',
                icon: 'toggle-notification',
                label: 'pam.accountMenu.notifications',
                link: { name: RouteName.BonusAndComms.Notifications },
                action: () => {},
                ...(numberOfNewMessages.value > 0 && {
                    unreadNotifications: numberOfNewMessages.value < 100 ? numberOfNewMessages.value : '99+',
                }),
            },
            {
                tag: 'chat',
                icon: 'actions-send-alt',
                label: 'pam.accountMenu.chat',
                link: { name: RouteName.Account.ChatPlaceholder },
                action: () => {},
                isLoginRequired: !isSocialAppEnabled(),
                hidden: !isSocialEnabledOrAppEnabled(),
            },
            {
                tag: 'promos',
                icon: 'commerce-gift',
                label: 'bonus_comms.promotion_hub_title',
                link: { name: RouteName.BonusAndComms.PromotionHub },
                action: () => onPromotionHubClick(PromotionHubEntryEventLabels.accountPageHeader),
                hidden: !(shellLocalConfig.bonus.isPromotionHubEntryPointVisible || !!user.value),
            },
            {
                tag: 'social',
                icon: 'human-group',
                label: 'pam.accountMenu.mySocial',
                link: { name: RouteName.Account.MySocial },
                action: () => {},
                isLoginRequired: true,
                hidden: !isSocialEnabledOrAppEnabled(),
            },
        ]);

        const filteredHeaderLinks = computed(() => {
            return headerLinks.value.filter(({ hidden }) => !hidden);
        });

        function onPromotionHubClick(analyticsEventLabel: string) {
            AnalyticsSDK.getInstance().log(
                'Promo_Hub',
                {
                    eventAction: 'Entry',
                    eventLabel: analyticsEventLabel,
                    userId: userId.value?.toString(),
                },
            );
        }

        const showLanguageSwitchIcon = ref(false);
        let featureSubscriptionUnsubscribeFn: () => void;

        onMounted(() => {
            featureSubscriptionUnsubscribeFn = featureFlagService.subscribeToFeatureChanges('language-switcher-in-account-menu', (value) => {
                showLanguageSwitchIcon.value = value && SiteLanguageConfig.hasLanguageSwitcher;
            }, false);
        });
        onBeforeUnmount(() => featureSubscriptionUnsubscribeFn?.());

        return {
            user,
            RouteName,
            filteredHeaderLinks,
            currentLocaleFlag: selectedLocaleObject?.flag,
            showLanguageSwitchIcon,
            isMobile: computed(() => isMobileLayout()),
            pageName: capitalize(translate('my account')),
            fullName: computed(() => user.value?.fullName || capitalize(translate('guest'))),
            breadcrumbs: [
                {
                    label: capitalize(translate('home')),
                    to: '/',
                },
            ],
            actions: [
                {
                    icon: 'status-help',
                    label: capitalize(translate('helpAndSupport')),
                    onClick: () => pushRoute({ name: RouteName.Account.HelpAndSupport }),
                },
                {
                    icon: 'navigation-settings',
                    label: capitalize(translate('settings')),
                    onClick: () => pushRoute({ name: RouteName.Account.Settings }),
                },
            ],
            currentNavigationContext,
        };
    },
    components: {
        SdsIcon,
        SdsFlag,
        HeaderLink,
        AccountUserInfoAvatar,
    },
});
